/**
 * ConnectionsPanel — backlinks ("Mentioned by") and outgoing links
 *
 * Place at: src/components/overlays/ConnectionsPanel.jsx
 * Mounted by: ProjectDetailOverlay.jsx (right after "Used in")
 * Backend:    backend/app/mentions_api.py  ->  GET /api/projects/<id>/connections
 *
 * Exports:
 *   useProjectConnections(projectId)  one fetch, shared by the two below
 *   MentionText                       renders a description, turning [[p:123]]
 *                                     into a clickable chip with the CURRENT name
 *   ConnectionsPanel (default)        "Mentioned by" / "Links to" / challenge
 *                                     answers / public collections pinning it
 *
 * Forks and parents are NOT repeated here -- the Creative Lineage tree above
 * already shows them. This panel is everything else that points at a project.
 *
 * FAILS QUIET: if the endpoint 404s (ENABLE_PROJECT_MENTIONS off, or the
 * project is not visible) the panel renders nothing and [[p:...]] tokens in
 * descriptions render as a muted "linked project". Safe to deploy before the
 * backend setting is on.
 *
 * Opening a linked project fetches the FULL project first, then calls
 * onViewProject -- the cards from /connections are deliberately small and the
 * overlay needs the whole record (description, author, files) to render.
 *
 * Zone colors are inline styles, not runtime Tailwind classes (purge-safe).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link2, CornerDownLeft, ArrowUpRight, EyeOff, Eye, Sparkles, Target, FolderTree } from 'lucide-react';
import { cn } from '../../utils/cn';
import { getProjectZone } from '../../config/zones';
import { mentionsAPI, api } from '../../api';

const MENTION_SPLIT = /(\[\[p:\d{1,9}\]\])/g;
const MENTION_ID = /^\[\[p:(\d{1,9})\]\]$/;
const FALLBACK_ZONE = { id: 'default', color: '#71717a', shortLabel: '?' };

/** /connections cards are camelCase; getProjectZone and the overlay read the
 *  snake_case project shape. Normalize once here. */
const normalize = (card) => ({
  ...card,
  project_type: card.projectType,
  image_url: card.imageUrl,
});

const zoneOf = (project) => {
  try {
    return getProjectZone(project) || FALLBACK_ZONE;
  } catch {
    return FALLBACK_ZONE;
  }
};


// ============================================================================
// DATA
// ============================================================================

export const useProjectConnections = (projectId) => {
  const [data, setData] = useState(null);          // null = loading or unavailable
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await mentionsAPI.connections(projectId);
      setData(res.data);
      setUnavailable(false);
    } catch {
      setData(null);
      setUnavailable(true);
    }
  }, [projectId]);

  useEffect(() => {
    setData(null);
    setUnavailable(false);
    load();
  }, [load]);

  /** Optimistic local update after hide/unhide. */
  const patchMentionedBy = useCallback((mentionId, isHidden) => {
    setData((prev) => {
      if (!prev) return prev;
      const mentionedBy = prev.mentionedBy.map((m) =>
        m.mentionId === mentionId ? { ...m, isHidden } : m);
      return {
        ...prev,
        mentionedBy,
        counts: { ...prev.counts, mentionedBy: mentionedBy.filter((m) => !m.isHidden).length },
      };
    });
  }, []);

  return { data, unavailable, reload: load, patchMentionedBy };
};

/** Fetch the whole project, then hand it to the overlay's navigation. */
const useOpenProject = (onViewProject) => {
  const [opening, setOpening] = useState(null);
  const open = useCallback(async (card) => {
    if (!onViewProject || opening) return;
    setOpening(card.id);
    try {
      const res = await api.get(`/projects/${card.id}`);
      onViewProject(res.data.project || res.data);
    } catch {
      // Could not load it (deleted, or visibility changed) -- stay put.
    } finally {
      setOpening(null);
    }
  }, [onViewProject, opening]);
  return { open, opening };
};


// ============================================================================
// DESCRIPTION WITH LINKED PROJECTS
// ============================================================================

export const MentionText = ({ text, connections, onViewProject, className }) => {
  const { open, opening } = useOpenProject(onViewProject);

  const byId = useMemo(() => {
    const map = new Map();
    for (const m of connections?.mentions || []) map.set(m.id, normalize(m));
    return map;
  }, [connections]);

  if (!text) return null;
  const parts = text.split(MENTION_SPLIT);

  return (
    <p className={className}>
      {parts.map((part, i) => {
        const match = part.match(MENTION_ID);
        if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;

        const target = byId.get(Number(match[1]));
        if (!target) {
          // Unknown, private, hidden, or still loading. Never show the raw id.
          return (
            <span key={i} className="inline-flex items-center gap-1 px-1.5 rounded-md text-zinc-600 bg-zinc-800/40 text-[0.92em]">
              <Link2 className="w-3 h-3" />
              linked project
            </span>
          );
        }

        const z = zoneOf(target);
        return (
          <button
            key={i}
            type="button"
            onClick={() => open(target)}
            disabled={opening === target.id}
            className="inline-flex items-center gap-1 px-1.5 rounded-md font-medium align-baseline
                       transition-colors hover:brightness-125 disabled:opacity-60"
            style={{ backgroundColor: `${z.color}18`, color: z.color }}
            title={`Open "${target.name}"`}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: z.color }} />
            {target.name}
          </button>
        );
      })}
    </p>
  );
};


// ============================================================================
// ONE LINKED PROJECT
// ============================================================================

const LinkRow = ({ card, homeZoneId, onOpen, opening, moderation }) => {
  const project = normalize(card);
  const z = zoneOf(project);
  const crossZone = z.id !== homeZoneId && z.id !== 'default';
  const img = project.image_url;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-2 rounded-xl border transition-all',
        card.isHidden
          ? 'bg-zinc-900/40 border-dashed border-zinc-800 opacity-60'
          : 'bg-zinc-800/25 border-zinc-800/50 hover:border-zinc-700/60',
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(project)}
        disabled={opening === project.id}
        className="flex items-center gap-3 flex-1 min-w-0 text-left group disabled:opacity-60"
      >
        <div
          className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0"
          style={{ border: `1.5px solid ${z.color}40` }}
        >
          {img ? (
            <img src={img} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center font-bold text-xs"
              style={{ backgroundColor: `${z.color}15`, color: z.color }}
            >
              {project.name?.[0]?.toUpperCase() || '?'}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-zinc-200 font-medium truncate group-hover:text-white transition-colors">
            {project.name}
          </p>
          <p className="text-[11px] text-zinc-500 truncate flex items-center gap-1.5">
            <span style={{ color: z.color }}>{z.shortLabel}</span>
            {project.author?.username && <span>@{project.author.username}</span>}
            {crossZone && !card.isHidden && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-fuchsia-400/80">
                <Sparkles className="w-2.5 h-2.5" />
                CROSS-ZONE
              </span>
            )}
          </p>
        </div>
      </button>

      {moderation && (
        <button
          type="button"
          onClick={() => moderation.toggle(card)}
          disabled={moderation.busy === card.mentionId}
          title={card.isHidden ? 'Show this backlink again' : 'Hide this backlink from everyone else'}
          aria-label={card.isHidden ? 'Unhide backlink' : 'Hide backlink'}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/60 transition-colors disabled:opacity-40 flex-shrink-0"
        >
          {card.isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
};

const Group = ({ icon: Icon, label, color, items, children }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold text-zinc-500 flex items-center gap-1.5 uppercase tracking-wide">
        <Icon className="w-3 h-3" style={{ color }} />
        {label}
        <span className="text-zinc-700 font-normal normal-case tracking-normal">{items.length}</span>
      </p>
      {children}
    </div>
  );
};


// ============================================================================
// PANEL
// ============================================================================

export const ConnectionsPanel = ({ connections, homeZoneId, onViewProject, onMentionHidden }) => {
  const { open, opening } = useOpenProject(onViewProject);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  if (!connections) return null;

  const {
    mentionedBy = [], mentions = [], responses = [], respondsTo = [],
    collections = [], canModerate = false,
  } = connections;

  const nothing = !mentionedBy.length && !mentions.length && !responses.length
    && !respondsTo.length && !collections.length;
  if (nothing) return null;

  const liveBacklinks = mentionedBy.filter((m) => !m.isHidden).length;

  const moderation = canModerate ? {
    busy,
    toggle: async (card) => {
      if (busy) return;
      setBusy(card.mentionId);
      setError(null);
      try {
        const res = await mentionsAPI.setHidden(card.mentionId, !card.isHidden);
        onMentionHidden?.(card.mentionId, res.data.isHidden);
      } catch (err) {
        setError(err?.response?.data?.error || 'Could not change that backlink');
      } finally {
        setBusy(null);
      }
    },
  } : null;

  return (
    <div className="mb-6">
      <h3 className="text-[13px] font-semibold text-zinc-300 mb-3 flex items-center gap-2">
        <Link2 className="w-3.5 h-3.5 text-sky-400" />
        Connected to
        {liveBacklinks > 0 && (
          <span className="text-[10px] text-sky-400/70 font-normal">
            {liveBacklinks} backlink{liveBacklinks !== 1 ? 's' : ''}
          </span>
        )}
      </h3>

      <div className="space-y-4">
        <Group icon={CornerDownLeft} label="Mentioned by" color="#38bdf8" items={mentionedBy}>
          {mentionedBy.map((card) => (
            <LinkRow
              key={`in-${card.mentionId}`}
              card={card}
              homeZoneId={homeZoneId}
              onOpen={open}
              opening={opening}
              moderation={moderation}
            />
          ))}
        </Group>

        <Group icon={ArrowUpRight} label="Links to" color="#a78bfa" items={mentions}>
          {mentions.map((card) => (
            <LinkRow key={`out-${card.mentionId}`} card={card} homeZoneId={homeZoneId} onOpen={open} opening={opening} />
          ))}
        </Group>

        <Group icon={Target} label="Answers a challenge on" color="#22c55e" items={respondsTo}>
          {respondsTo.map((card) => (
            <LinkRow key={`rt-${card.id}`} card={card} homeZoneId={homeZoneId} onOpen={open} opening={opening} />
          ))}
        </Group>

        <Group icon={Target} label="Challenge answers" color="#22c55e" items={responses}>
          {responses.map((card) => (
            <LinkRow key={`rs-${card.id}`} card={card} homeZoneId={homeZoneId} onOpen={open} opening={opening} />
          ))}
        </Group>

        <Group icon={FolderTree} label="Pinned in" color="#c084fc" items={collections}>
          <div className="flex flex-wrap gap-1.5">
            {collections.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-zinc-800/60 text-zinc-300 border border-zinc-700/50"
                title={c.owner?.username ? `Collection by @${c.owner.username}` : undefined}
              >
                {c.name}
                {c.owner?.username && <span className="text-zinc-600">@{c.owner.username}</span>}
              </span>
            ))}
          </div>
        </Group>
      </div>

      {error && <p className="mt-2 text-[11px] text-red-400">{error}</p>}
    </div>
  );
};

export default ConnectionsPanel;